import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Input, Radio, Tag, Tooltip } from 'antd';
import { ze } from 'utils/zhEn';

const SYSTEM_MODULE_RE =
  /^(?:libc\.so.*|libm\.so.*|libdl\.so.*|libart\.so.*|libandroid_runtime\.so.*|libbinder\.so.*|libutils\.so.*|libcutils\.so.*|libgui\.so.*|libhwui\.so.*|libvulkan\.so.*|libEGL\.so.*|libGLES.*|base\.odex|boot.*\.oat|\[vdso\]|libsystem_.*\.dylib|libSystem\.B\.dylib|libobjc\.A\.dylib|libdispatch\.dylib|libc\+\+.*\.dylib|dyld|CoreFoundation|Foundation|UIKitCore|UIKit|GraphicsServices|CFNetwork|QuartzCore|AudioToolbox|Metal|MetalKit|RunningBoardServices|FrontBoardServices|ntdll\.dll|kernel32\.dll|kernelbase\.dll|user32\.dll|gdi32\.dll|win32u\.dll|advapi32\.dll|msvcrt\.dll|ucrtbase\.dll|vcruntime\d+.*\.dll|msvcp\d+.*\.dll|d3d11\.dll|d3d12\.dll|dxgi\.dll|opengl32\.dll|vulkan-1\.dll|nvwgf2umx\.dll|amdxc64\.dll|mswsock\.dll|ws2_32\.dll|libpthread\.so.*|libstdc\+\+\.so.*|libgcc_s\.so.*|ld-linux.*\.so.*|librt\.so.*|libc-\d+\.\d+\.so)$/i;

const SYSTEM_SYMBOL_PREFIX_RE =
  /^(?:java\.(?:lang|util|io|net|nio|security|math)\.|javax\.|sun\.misc\.|com\.android\.|android\.(?:os|app|view|content|widget|hardware|net|graphics|database)\.|dalvik\.system\.|libcore\.io\.|kotlin\.(?:coroutines|jvm|io)\.|kotlinx\.coroutines\.|okio\.|com\.android\.okhttp\.)/i;

const SDK_SELF_CAPTURE_RE =
  /\b(?:com\.uqm\.crashsight|libCrashSight|CrashSightCore|CrashSightReport|CrashSight\.dll|handleNativeException2|packageCrashDatas)\b/i;

const ENGINE_MODULE_OR_SYMBOL_RE =
  /\b(?:libunity\.so|UnityPlayer\.dll|UnityFramework|com\.unity3d\.player|libUE4\.so|UnrealEngine|UE4Game|libcocos2d|libgodot|il2cpp::vm::|il2cpp_runtime_invoke)\b|[\\/]Engine[\\/]Source[\\/]/i;

const SYSTEM_HEADER_PATH_RE =
  /(?:Windows Kits|Microsoft Visual Studio)[\\/]|(?:^|[\\/])(?:corecrt_\w+\.h|vcruntime\w*\.h)$/i;

const IN_APP_SCRIPT_FILE_RE = /\.(?:cs|lua|js|mjs|ts)\s*:\s*\d+|\bInGamePath:/i;

const IN_APP_MODULE_RE =
  /\b(?:libil2cpp\.so|GameAssembly\.dll|libpuerts\.so|libxlua\.so|libslua\.so|libtolua\.so|Lua|libQts\w*\.so|sgame_unity|UAGame|CAGame|NZM|LetsGo|Shipping|GameCore)\b/i;

export function classifyStackFrameTier(lineObjOrText, pid) {
  const isObj = lineObjOrText && typeof lineObjOrText === 'object';
  const rawLine = String(
    isObj ? lineObjOrText.rawLine || lineObjOrText.line || '' : lineObjOrText || ''
  ).trim();
  const soName = String(
    isObj ? lineObjOrText.soNameWithoutPath || lineObjOrText.libname || '' : ''
  )
    .trim()
    .split('/')
    .pop();
  const filePath = String(isObj ? lineObjOrText.filePath || '' : '').trim();
  const extraInfo = (isObj && lineObjOrText.extraInfo) || {};

  // 1. Explicit SDK self-capture or system/runtime frame
  if (SDK_SELF_CAPTURE_RE.test(rawLine) || SDK_SELF_CAPTURE_RE.test(soName)) {
    return {
      tier: 'system',
      labelZh: 'SDK采样',
      labelEn: 'SDK',
      badgeColor: 'default',
    };
  }

  if (
    extraInfo.isSystemModule === true ||
    extraInfo.nullableSkipStackInfo?.type === 1 ||
    (soName && SYSTEM_MODULE_RE.test(soName)) ||
    SYSTEM_SYMBOL_PREFIX_RE.test(rawLine) ||
    SYSTEM_HEADER_PATH_RE.test(filePath) ||
    SYSTEM_HEADER_PATH_RE.test(rawLine)
  ) {
    return {
      tier: 'system',
      labelZh: '系统/运行时',
      labelEn: 'System',
      badgeColor: 'default',
    };
  }

  // Check if rawLine contains an explicit system module path (e.g. "/system/lib64/libc.so" or "/apex/.../libart.so" or "base.odex")
  if (
    /(?:\/system\/lib|\/apex\/com\.android\.|\/usr\/lib\/system\/|base\.odex|boot(?:-framework)?\.oat|\bntdll\.dll\b|\bKERNELBASE\.dll\b|\bkernel32\.dll\b|\blibc\.so\b|\blibart\.so\b)/i.test(
      rawLine
    ) &&
    !IN_APP_MODULE_RE.test(soName) &&
    !ENGINE_MODULE_OR_SYMBOL_RE.test(soName) &&
    !ENGINE_MODULE_OR_SYMBOL_RE.test(rawLine)
  ) {
    return {
      tier: 'system',
      labelZh: '系统/运行时',
      labelEn: 'System',
      badgeColor: 'default',
    };
  }

  // 2. Explicit script file (.cs / .lua / .js / .ts) is always In-App
  if (IN_APP_SCRIPT_FILE_RE.test(rawLine) || IN_APP_SCRIPT_FILE_RE.test(filePath)) {
    return {
      tier: 'in_app',
      labelZh: '业务/脚本',
      labelEn: 'In-App',
      badgeColor: 'blue',
    };
  }

  // 3. Game Engine core (libunity.so, UnityPlayer.dll, libUE4.so, Engine/Source/..., il2cpp::vm::Runtime::Invoke, etc.)
  if (
    ENGINE_MODULE_OR_SYMBOL_RE.test(soName) ||
    ENGINE_MODULE_OR_SYMBOL_RE.test(filePath) ||
    ENGINE_MODULE_OR_SYMBOL_RE.test(rawLine) ||
    /^(?:libil2cpp|il2cpp)\/vm\//i.test(filePath)
  ) {
    return {
      tier: 'engine',
      labelZh: '引擎层',
      labelEn: 'Engine',
      badgeColor: 'purple',
    };
  }

  // 4. In-App business / IL2CPP generated C# / Puerts / Lua / game binary or custom native plugin
  if (
    IN_APP_MODULE_RE.test(soName) ||
    IN_APP_MODULE_RE.test(rawLine) ||
    (filePath && !ENGINE_MODULE_OR_SYMBOL_RE.test(soName))
  ) {
    return {
      tier: 'in_app',
      labelZh: '业务/脚本',
      labelEn: 'In-App',
      badgeColor: 'blue',
    };
  }

  // 5. Other non-system loaded modules with a known module name
  if (soName && soName !== '<unknown>' && !SYSTEM_MODULE_RE.test(soName)) {
    return {
      tier: 'in_app',
      labelZh: '应用模块',
      labelEn: 'App',
      badgeColor: 'blue',
    };
  }

  return {
    tier: 'system',
    labelZh: '系统/运行时',
    labelEn: 'System',
    badgeColor: 'default',
  };
}

export function buildSmartFoldedStackRows(
  stack = [],
  pid,
  collapseMode = 'smart',
  expandedChunkKeys = {}
) {
  if (!Array.isArray(stack) || stack.length === 0) {
    return {
      rows: [],
      stats: { inAppCount: 0, engineCount: 0, systemCount: 0, foldedCount: 0 },
    };
  }

  const annotated = stack.map((lineObj, index) => {
    const tierInfo = classifyStackFrameTier(lineObj, pid);
    return {
      lineObj,
      index,
      tierInfo,
      isDefinitive: Boolean(lineObj?.extraInfo?.isDefinitive),
    };
  });

  let inAppCount = 0;
  let engineCount = 0;
  let systemCount = 0;
  annotated.forEach((item) => {
    if (item.tierInfo.tier === 'in_app') inAppCount += 1;
    else if (item.tierInfo.tier === 'engine') engineCount += 1;
    else systemCount += 1;
  });

  const isInAppFocusMode = collapseMode === 'in_app';
  const nonInAppCount = engineCount + systemCount;
  const canFold =
    collapseMode !== 'full' &&
    stack.length > 3 &&
    (isInAppFocusMode
      ? inAppCount > 0 && nonInAppCount >= 2
      : (inAppCount > 0 || engineCount > 0) && systemCount >= 2);

  if (!canFold) {
    return {
      rows: annotated.map((item) => ({
        type: 'frame',
        ...item,
      })),
      stats: { inAppCount, engineCount, systemCount, foldedCount: 0 },
    };
  }

  const shouldFoldItem = (item) => {
    if (item.isDefinitive) return false;
    if (isInAppFocusMode) {
      return item.tierInfo.tier !== 'in_app';
    }
    return item.tierInfo.tier === 'system';
  };

  const rows = [];
  let foldedCount = 0;
  let i = 0;
  while (i < annotated.length) {
    const cur = annotated[i];
    // Never fold index 0 (top of stack) or definitive key frames
    if (i > 0 && shouldFoldItem(cur)) {
      let j = i;
      while (j < annotated.length && shouldFoldItem(annotated[j])) {
        j += 1;
      }
      const chunkLen = j - i;
      if (chunkLen >= 2) {
        const chunkItems = annotated.slice(i, j);
        const chunkKey = `fold-${collapseMode}-${i}-${j - 1}`;
        const moduleNames = [];
        chunkItems.forEach((ci) => {
          const mod =
            ci.lineObj?.soNameWithoutPath ||
            ci.lineObj?.libname ||
            String(ci.lineObj?.line || '')
              .split('(')[0]
              .split('.')
              .slice(0, 2)
              .join('.');
          if (mod && !moduleNames.includes(mod) && moduleNames.length < 3) {
            moduleNames.push(mod);
          }
        });
        const isExpanded = Boolean(expandedChunkKeys[chunkKey]);
        const hasEngineInChunk = chunkItems.some((ci) => ci.tierInfo.tier === 'engine');
        rows.push({
          type: 'folded_system',
          chunkKey,
          startIndex: i,
          endIndex: j - 1,
          count: chunkLen,
          hasEngineInChunk,
          modulesSummary: moduleNames.join(', '),
          isExpanded,
          items: chunkItems,
        });
        if (isExpanded) {
          chunkItems.forEach((ci) => {
            rows.push({
              type: 'frame',
              ...ci,
              parentChunkKey: chunkKey,
            });
          });
        } else {
          foldedCount += chunkLen;
        }
        i = j;
        continue;
      }
    }
    rows.push({
      type: 'frame',
      ...cur,
    });
    i += 1;
  }

  return {
    rows,
    stats: { inAppCount, engineCount, systemCount, foldedCount },
  };
}

function extractThreadPoolPrefix(threadName) {
  const clean = String(threadName || '')
    .replace(/\(\d+\)\s*$/, '')
    .trim();
  const poolMatch = clean.match(/^([A-Za-z0-9_.-]+?)[-#_]\d+(?:[-#_]\d+)?$/);
  if (poolMatch) {
    return poolMatch[1];
  }
  if (/^pool-\d+-thread-\d+$/i.test(clean)) {
    return 'pool-*-thread';
  }
  return clean || 'Thread';
}

export function classifySingleThreadState(threadObj) {
  const fileName = String(threadObj?.fileName || '').trim();
  const rawLines = String(threadObj?.content || '')
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  const topFrame = rawLines[0] || '';
  const top3Text = rawLines.slice(0, 3).join(' | ');

  // 1. SDK self-capture thread (e.g. CrashSightNativeException)
  if (
    /CrashSightNativeException|CrashSight.*Thread|Bugly/i.test(fileName) ||
    /VMStack\.getThreadStackTrace|Thread\.getAllStackTraces|handleNativeException2|packageCrashDatas/i.test(
      top3Text
    )
  ) {
    return {
      category: 'sdk_capture',
      stateLabelZh: 'SDK 抓栈采样线程',
      stateLabelEn: 'SDK Capture Thread',
      waitPrimitive: 'VMStack.getThreadStackTrace',
      poolPrefix: 'CrashSightSDK',
      topFrame,
      lineCount: rawLines.length,
    };
  }

  // 2. Lock contention or synchronous I/O blocking
  if (
    /monitorEnter|pthread_mutex_lock|__futex_abstimed_wait.*mutex|RtlEnterCriticalSection|WaitForCriticalSection|std::mutex::lock|os_unfair_lock_lock|ulock_wait/i.test(
      top3Text
    )
  ) {
    return {
      category: 'blocked_io_lock',
      subKind: 'lock',
      stateLabelZh: '锁争用等待 (Mutex / Monitor)',
      stateLabelEn: 'Lock Contention',
      waitPrimitive: topFrame.split('(')[0] || 'MutexWait',
      poolPrefix: extractThreadPoolPrefix(fileName),
      topFrame,
      lineCount: rawLines.length,
    };
  }

  if (
    /DatagramSocket\.receive|recvfromBytes|recvfrom|SocketInputStream\.socketRead|PlainSocketImpl\.socketConnect|connectErrno|Linux\.pread|Linux\.readBytes|FileInputStream\.readBytes|NtReadFile|NtWriteFile/i.test(
      top3Text
    )
  ) {
    const ioMethod =
      topFrame.match(
        /(DatagramSocket\.receive|Linux\.recvfromBytes|SocketInputStream\.socketRead|PlainSocketImpl\.socketConnect|IoBridge\.connectErrno|NtReadFile|NtWriteFile)/i
      )?.[1] ||
      topFrame.split('(')[0] ||
      'IO_Read';
    const callerFrame =
      rawLines.find(
        (l, idx) =>
          idx > 0 &&
          !/^(?:java\.|libcore\.|android\.|kotlin\.|kotlinx\.|sun\.|com\.android\.)/i.test(l)
      ) ||
      rawLines[1] ||
      topFrame;
    return {
      category: 'blocked_io_lock',
      subKind: 'io',
      stateLabelZh: `同步 I/O 阻塞 (${ioMethod})`,
      stateLabelEn: `Blocking I/O (${ioMethod})`,
      waitPrimitive: ioMethod,
      callerSummary: callerFrame,
      poolPrefix: extractThreadPoolPrefix(fileName),
      topFrame,
      lineCount: rawLines.length,
    };
  }

  // 3. Standard Idle / Parked / Looper Poll primitives
  const idleMatch = top3Text.match(
    /(MessageQueue\.nativePollOnce|Unsafe\.park|Object\.wait|Thread\.sleep|NtWaitForSingleObject|NtWaitForMultipleObjects|NtWaitForAlertByThreadId|NtWaitForWorkViaWorkerFactory|NtRemoveIoCompletion(?:Ex)?|NtDelayExecution|NtUserMsgWaitForMultipleObjectsEx|NtUserGetMessage|WaitForSingleObject(?:Ex)?|WaitForMultipleObjects(?:Ex)?|mach_msg_trap|__psynch_cvwait|__semwait_signal|__workq_kernreturn|epoll_wait|epoll_pwait|futex_wait)/i
  );
  if (idleMatch) {
    const primitive = idleMatch[1];
    return {
      category: 'idle_parked',
      stateLabelZh: `空闲挂起 (${primitive})`,
      stateLabelEn: `Idle (${primitive})`,
      waitPrimitive: primitive,
      poolPrefix: extractThreadPoolPrefix(fileName),
      topFrame,
      lineCount: rawLines.length,
    };
  }

  // 4. Active / Executing Thread!
  return {
    category: 'active',
    stateLabelZh: '活跃执行中',
    stateLabelEn: 'Active / Executing',
    waitPrimitive: null,
    poolPrefix: extractThreadPoolPrefix(fileName),
    topFrame,
    lineCount: rawLines.length,
  };
}

export function analyzeAllThreadsConcurrency(threads = [], reportThreadName = '') {
  const cleanedReportName = String(reportThreadName || '').trim();
  const validThreads = [];
  (threads || []).forEach((t, originalIndex) => {
    if (!t || !t.fileName) return;
    const tName = String(t.fileName).trim();
    if (cleanedReportName && tName === cleanedReportName) {
      return;
    }
    const info = classifySingleThreadState(t);
    validThreads.push({
      originalIndex,
      fileName: tName,
      content: t.content || '',
      otherThreadExtraInfo: t.otherThreadExtraInfo,
      ...info,
    });
  });

  const activeThreads = validThreads.filter((t) => t.category === 'active');
  const blockedThreads = validThreads.filter((t) => t.category === 'blocked_io_lock');
  const idleThreads = validThreads.filter(
    (t) => t.category === 'idle_parked' || t.category === 'sdk_capture'
  );

  // Group blocked threads by poolPrefix + waitPrimitive
  const blockedGroupMap = new Map();
  blockedThreads.forEach((t) => {
    const key = `${t.poolPrefix}::${t.waitPrimitive}`;
    if (!blockedGroupMap.has(key)) {
      blockedGroupMap.set(key, {
        groupKey: `blocked-${blockedGroupMap.size}`,
        poolPrefix: t.poolPrefix,
        waitPrimitive: t.waitPrimitive,
        stateLabelZh: t.stateLabelZh,
        stateLabelEn: t.stateLabelEn,
        subKind: t.subKind,
        callerSummary: t.callerSummary || t.topFrame,
        threads: [],
      });
    }
    blockedGroupMap.get(key).threads.push(t);
  });

  // Group idle threads by waitPrimitive so 47 Looper threads & 43 Unsafe.park threads form clean pools
  const idleGroupMap = new Map();
  idleThreads.forEach((t) => {
    const key = t.category === 'sdk_capture' ? 'SDK_CAPTURE' : t.waitPrimitive || 'Idle';
    if (!idleGroupMap.has(key)) {
      idleGroupMap.set(key, {
        groupKey: `idle-${idleGroupMap.size}`,
        waitPrimitive: key,
        stateLabelZh: t.stateLabelZh,
        stateLabelEn: t.stateLabelEn,
        threads: [],
        poolCounts: {},
      });
    }
    const grp = idleGroupMap.get(key);
    grp.threads.push(t);
    grp.poolCounts[t.poolPrefix] = (grp.poolCounts[t.poolPrefix] || 0) + 1;
  });

  const idleGroups = Array.from(idleGroupMap.values())
    .map((grp) => {
      const entries = Object.entries(grp.poolCounts).sort((a, b) => {
        if (a[0] === 'main') return -1;
        if (b[0] === 'main') return 1;
        return b[1] - a[1];
      });
      const topPools = entries
        .slice(0, 6)
        .map(([name, cnt]) => (cnt > 1 ? `${name} ×${cnt}` : name));
      const suffix =
        entries.length > 6 ? ze(` 等 ${grp.threads.length} 个线程`, ` etc. (${grp.threads.length})`) : '';
      return {
        ...grp,
        topPoolsSummary: `${topPools.join('、')}${suffix}`,
      };
    })
    .sort((a, b) => b.threads.length - a.threads.length);

  return {
    totalThreadCount: validThreads.length,
    activeThreads,
    blockedGroups: Array.from(blockedGroupMap.values()),
    blockedThreadCount: blockedThreads.length,
    idleGroups,
    idleThreadCount: idleThreads.length,
  };
}

const AllThreadsPanorama = ({
  threads = [],
  reportThreadName = '',
  renderThreadItem,
}) => {
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [expandedPoolKeys, setExpandedPoolKeys] = useState({});
  const [expandedThreadIndices, setExpandedThreadIndices] = useState({});

  const filteredInputThreads = useMemo(() => {
    const kw = String(searchKeyword || '').trim().toLowerCase();
    if (!kw) return threads;
    return (threads || []).filter((t) => {
      const text = `${t?.fileName || ''}\n${t?.content || ''}`.toLowerCase();
      return text.includes(kw);
    });
  }, [threads, searchKeyword]);

  const panorama = useMemo(
    () => analyzeAllThreadsConcurrency(filteredInputThreads, reportThreadName),
    [filteredInputThreads, reportThreadName]
  );

  const {
    totalThreadCount,
    activeThreads,
    blockedGroups,
    blockedThreadCount,
    idleGroups,
    idleThreadCount,
  } = panorama;

  const showActive = categoryFilter === 'all' || categoryFilter === 'active';
  const showBlocked = categoryFilter === 'all' || categoryFilter === 'blocked';
  const showIdle = categoryFilter === 'all' || categoryFilter === 'idle';

  return (
    <div
      data-testid="all-threads-panorama"
      style={{
        marginTop: '12px',
        padding: '12px 16px',
        background: '#fafafa',
        border: '1px solid #f0f0f0',
        borderRadius: '6px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '12px',
          paddingBottom: '10px',
          borderBottom: '1px solid #e8e8e8',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: '13px', color: '#262626' }}>
            {ze('🧭 全线程并发状态全景', '🧭 All-Threads Concurrency Panorama')}
          </span>
          <Tag color="geekblue" style={{ margin: 0 }}>
            {ze(`共 ${totalThreadCount} 个其他线程`, `${totalThreadCount} Other Threads`)}
          </Tag>
          <Tag
            color="volcano"
            style={{ margin: 0, cursor: 'pointer' }}
            onClick={() =>
              setCategoryFilter((prev) => (prev === 'active' ? 'all' : 'active'))
            }
          >
            {ze(
              `🔥 活跃执行 ${activeThreads.length}`,
              `🔥 Active ${activeThreads.length}`
            )}
          </Tag>
          {blockedThreadCount > 0 && (
            <Tag
              color="orange"
              style={{ margin: 0, cursor: 'pointer' }}
              onClick={() =>
                setCategoryFilter((prev) => (prev === 'blocked' ? 'all' : 'blocked'))
              }
            >
              {ze(
                `🔒 锁等待 / I/O 阻塞 ${blockedThreadCount}`,
                `🔒 Blocked / I/O ${blockedThreadCount}`
              )}
            </Tag>
          )}
          <Tag
            color="default"
            style={{ margin: 0, cursor: 'pointer' }}
            onClick={() =>
              setCategoryFilter((prev) => (prev === 'idle' ? 'all' : 'idle'))
            }
          >
            {ze(
              `💤 空闲挂起池 ${idleThreadCount}（已聚合成 ${idleGroups.length} 组）`,
              `💤 Idle ${idleThreadCount} (${idleGroups.length} pools)`
            )}
          </Tag>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Input.Search
            allowClear
            size="small"
            style={{ width: '220px' }}
            placeholder={ze('搜索线程名或堆栈函数...', 'Search thread or symbol...')}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />
          <Radio.Group
            size="small"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <Radio.Button value="all">{ze('全部状态', 'All')}</Radio.Button>
            <Radio.Button value="active">
              {ze(`活跃 (${activeThreads.length})`, `Active (${activeThreads.length})`)}
            </Radio.Button>
            {blockedThreadCount > 0 && (
              <Radio.Button value="blocked">
                {ze(`阻塞 (${blockedThreadCount})`, `Blocked (${blockedThreadCount})`)}
              </Radio.Button>
            )}
            <Radio.Button value="idle">
              {ze(`空闲池 (${idleThreadCount})`, `Idle (${idleThreadCount})`)}
            </Radio.Button>
          </Radio.Group>
        </div>
      </div>

      {/* 1. Active Executing Threads (expanded by default) */}
      {showActive && activeThreads.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <div
            style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#cf1322',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span>
              {ze(
                `🔥 活跃执行线程（${activeThreads.length} 个，崩溃时刻正在执行业务/引擎/JNI调用）`,
                `🔥 Active Executing Threads (${activeThreads.length})`
              )}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {activeThreads.map((t, activeIdx) => {
              const defaultCollapsed = activeIdx >= 5;
              const isCollapsed =
                t.originalIndex in expandedThreadIndices
                  ? Boolean(expandedThreadIndices[t.originalIndex])
                  : defaultCollapsed;
              return (
                <div
                  key={`active-${t.originalIndex}`}
                  style={{
                    border: '1px solid #ffa39e',
                    borderRadius: '4px',
                    background: '#ffffff',
                    overflow: 'hidden',
                  }}
                >
                  {renderThreadItem(t, t.originalIndex, isCollapsed, () =>
                    setExpandedThreadIndices((prev) => ({
                      ...prev,
                      [t.originalIndex]: !isCollapsed,
                    }))
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 2. Blocked / Synchronous I/O Threads */}
      {showBlocked && blockedGroups.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <div
            style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#d46b08',
              marginBottom: '8px',
            }}
          >
            {ze(
              `🔒 锁等待 / 同步 I/O 阻塞线程组（共 ${blockedThreadCount} 个线程，聚合成 ${blockedGroups.length} 组）`,
              `🔒 Lock / Blocking I/O Thread Groups (${blockedThreadCount} threads in ${blockedGroups.length} groups)`
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {blockedGroups.map((grp) => {
              const isGroupExpanded = Boolean(expandedPoolKeys[grp.groupKey]);
              return (
                <div
                  key={grp.groupKey}
                  style={{
                    padding: '8px 12px',
                    background: '#fffbe6',
                    border: '1px solid #ffe58f',
                    borderRadius: '4px',
                    fontSize: '12px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Tag color="orange" style={{ margin: 0, fontWeight: 600 }}>
                      {`${grp.poolPrefix} ×${grp.threads.length}`}
                    </Tag>
                    <Tag color="gold" style={{ margin: 0 }}>
                      {ze(grp.stateLabelZh, grp.stateLabelEn)}
                    </Tag>
                    <span
                      style={{
                        fontFamily: 'monospace',
                        color: '#434343',
                        flex: 1,
                        wordBreak: 'break-all',
                      }}
                    >
                      {grp.callerSummary}
                    </span>
                    <a
                      style={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                      onClick={() =>
                        setExpandedPoolKeys((prev) => ({
                          ...prev,
                          [grp.groupKey]: !prev[grp.groupKey],
                        }))
                      }
                    >
                      {isGroupExpanded
                        ? ze('收起线程组 ▴', 'Collapse ▴')
                        : ze(
                            `展开查看 ${grp.threads.length} 个线程堆栈 ▾`,
                            `Expand ${grp.threads.length} threads ▾`
                          )}
                    </a>
                  </div>
                  {isGroupExpanded && (
                    <div
                      style={{
                        marginTop: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      {grp.threads.map((t) => {
                        const isCollapsed = Boolean(expandedThreadIndices[t.originalIndex]);
                        return (
                          <div
                            key={`blk-${t.originalIndex}`}
                            style={{
                              background: '#ffffff',
                              border: '1px solid #f0f0f0',
                              borderRadius: '4px',
                            }}
                          >
                            {renderThreadItem(t, t.originalIndex, isCollapsed, () =>
                              setExpandedThreadIndices((prev) => ({
                                ...prev,
                                [t.originalIndex]: !prev[t.originalIndex],
                              }))
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 3. Idle / Parked Thread Pools */}
      {showIdle && idleGroups.length > 0 && (
        <div>
          <div
            style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#595959',
              marginBottom: '8px',
            }}
          >
            {ze(
              `💤 空闲挂起线程池（共 ${idleThreadCount} 个空闲线程，已自动按等待原语聚合成 ${idleGroups.length} 组）`,
              `💤 Idle / Parked Thread Pools (${idleThreadCount} threads in ${idleGroups.length} pools)`
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {idleGroups.map((grp) => {
              const isGroupExpanded = Boolean(expandedPoolKeys[grp.groupKey]);
              return (
                <div
                  key={grp.groupKey}
                  style={{
                    padding: '6px 12px',
                    background: '#ffffff',
                    border: '1px solid #e8e8e8',
                    borderRadius: '4px',
                    fontSize: '12px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Tag style={{ margin: 0, fontFamily: 'monospace', fontWeight: 600 }}>
                      {`${grp.threads.length} ${ze('个线程', 'threads')}`}
                    </Tag>
                    <Tag color="default" style={{ margin: 0 }}>
                      {ze(grp.stateLabelZh, grp.stateLabelEn)}
                    </Tag>
                    <Tooltip title={grp.threads.map((t) => t.fileName).join(', ')}>
                      <span
                        style={{
                          color: '#595959',
                          flex: 1,
                          wordBreak: 'break-all',
                        }}
                      >
                        {`${ze('包含线程：', 'Threads: ')}${grp.topPoolsSummary}`}
                      </span>
                    </Tooltip>
                    <a
                      style={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                      onClick={() =>
                        setExpandedPoolKeys((prev) => ({
                          ...prev,
                          [grp.groupKey]: !prev[grp.groupKey],
                        }))
                      }
                    >
                      {isGroupExpanded
                        ? ze('收起空闲池 ▴', 'Collapse ▴')
                        : ze(
                            `展开 ${grp.threads.length} 个空闲线程 ▾`,
                            `Expand ${grp.threads.length} idle threads ▾`
                          )}
                    </a>
                  </div>
                  {isGroupExpanded && (
                    <div
                      style={{
                        marginTop: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      {grp.threads.map((t) => {
                        const isCollapsed =
                          t.originalIndex in expandedThreadIndices
                            ? !expandedThreadIndices[t.originalIndex]
                            : grp.threads.length > 5;
                        return (
                          <div
                            key={`idle-${t.originalIndex}`}
                            style={{
                              background: '#fafafa',
                              border: '1px solid #f0f0f0',
                              borderRadius: '4px',
                            }}
                          >
                            {renderThreadItem(t, t.originalIndex, isCollapsed, () =>
                              setExpandedThreadIndices((prev) => ({
                                ...prev,
                                [t.originalIndex]: isCollapsed,
                              }))
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

AllThreadsPanorama.propTypes = {
  threads: PropTypes.array,
  reportThreadName: PropTypes.string,
  renderThreadItem: PropTypes.func.isRequired,
};

export default AllThreadsPanorama;
